import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallEnd01Icon,
  Cancel01Icon,
  Mic01Icon,
  MicOff01Icon,
  UserAdd01Icon,
  UserCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { useSoftphone } from "./SoftphoneProvider";
import { describeCall, type CallDisplay } from "./callDisplay";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
const ENDED_AVATAR_COLOR = "#9CA3AF";

interface CallScreenProps {
  // Live in-call duration, ticked once a second by the overlay.
  elapsedSeconds: number;
  // Collapse the expanded card back to the floating pill.
  onMinimize: () => void;
  // Opens the in-call Create-lead form (inbound calls with no matched lead).
  onCreateLead: () => void;
  // Drag position, owned by the overlay so it survives minimize/expand.
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Expanded in-call card (CRM-811, Figma 22148:162504; inbound states CRM-905,
 * Figma 23472:90575). Floats bottom-right and walks the call through Ringing →
 * mm:ss (with mute) → No answer / Call ended · mm:ss / Can't reach this
 * number. Inbound calls add a middle action: Contact details (matched lead) or
 * Create lead. The X collapses a live call to the pill; for a terminal call it
 * dismisses the card. Drag it anywhere on screen to uncover what it sits on
 * top of (CRM-1112).
 */
export function CallScreen({
  elapsedSeconds,
  onMinimize,
  onCreateLead,
  dragOffset,
  onDragOffsetChange
}: CallScreenProps) {
  const {
    activeCall,
    status,
    isMuted,
    callDurationSeconds,
    hangup,
    toggleMute,
    dismissCall
  } = useSoftphone();

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  if (!activeCall) return null;

  const view = describeCall(status, { elapsedSeconds, callDurationSeconds });
  const displayName = activeCall.toLabel?.trim();
  const displayNumber = formatPhoneDisplay(activeCall.toNumber);
  const isInbound = activeCall.direction === "inbound";

  return (
    <div
      ref={nodeRef}
      role="dialog"
      aria-label="Active call"
      style={style}
      {...dragProps}
      className={cx(
        "fixed bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-alpha-100",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        aria-label={view.isTerminal ? "Dismiss" : "Minimize"}
        onClick={view.isTerminal ? dismissCall : onMinimize}
        className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={20} />
      </button>

      <div className="flex flex-col items-center text-center">
        <Avatar
          name={displayName || displayNumber}
          size={72}
          className="text-2xl font-semibold"
          fallbackBackgroundColor={
            view.isLive ? ACTIVE_AVATAR_COLOR : ENDED_AVATAR_COLOR
          }
        />

        <p className="mt-4 text-base font-semibold text-content-1000">
          {displayNumber}
        </p>
        {displayName ? (
          <p className="mt-0.5 text-sm text-content-400">{displayName}</p>
        ) : null}

        <div className="mt-8 min-h-12">
          <p
            className={cx(
              "text-lg font-semibold tabular-nums",
              view.tone === "error" ? "text-danger-500" : "text-content-1000"
            )}
          >
            {view.statusLine}
          </p>
          {view.detail ? (
            <p className="mx-auto mt-1 max-w-[260px] text-xs text-danger-400">
              {view.detail}
            </p>
          ) : null}
        </div>

        <CallControls
          view={view}
          isMuted={isMuted}
          onHangup={hangup}
          onToggleMute={toggleMute}
          // Lead actions only make sense on an answered inbound call (CRM-905).
          leadId={isInbound && view.showMute ? activeCall.leadId : undefined}
          onCreateLead={
            isInbound && view.showMute && !activeCall.leadId
              ? onCreateLead
              : undefined
          }
        />
      </div>
    </div>
  );
}

interface CallControlsProps {
  view: CallDisplay;
  isMuted: boolean;
  onHangup: () => void;
  onToggleMute: () => void;
  // Inbound-only middle action: link to the matched lead…
  leadId?: number;
  // …or open the in-call Create-lead form when nothing matched.
  onCreateLead?: () => void;
}

function CallControls({
  view,
  isMuted,
  onHangup,
  onToggleMute,
  leadId,
  onCreateLead
}: CallControlsProps) {
  if (!view.showHangup && !view.showMute) return null;

  return (
    <div className="mt-6 flex items-start justify-center gap-10">
      {view.showHangup ? (
        <ControlButton
          label="End call"
          onClick={onHangup}
          circleClassName="bg-danger-500 text-white hover:bg-danger-600"
          icon={CallEnd01Icon}
        />
      ) : null}
      {leadId ? (
        // Plain anchor (the overlay mounts outside the router) opening a NEW
        // tab — a same-tab navigation would unload the SPA and destroy the
        // Twilio Device, dropping the live call.
        <a
          href={`/leads/${leadId}`}
          target="_blank"
          rel="noreferrer"
          className="flex flex-col items-center gap-2"
        >
          <span className="grid size-12 place-items-center rounded-full bg-alpha-40 text-content-1000 transition hover:bg-alpha-100">
            <HugeiconsIcon icon={UserCircleIcon} size={22} />
          </span>
          <span className="text-xs text-content-400">Contact details</span>
        </a>
      ) : null}
      {onCreateLead ? (
        <ControlButton
          label="Create lead"
          onClick={onCreateLead}
          circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
          icon={UserAdd01Icon}
        />
      ) : null}
      {view.showMute ? (
        <ControlButton
          label={isMuted ? "Unmute" : "Mute"}
          onClick={onToggleMute}
          circleClassName={cx(
            "text-content-1000 hover:bg-alpha-100",
            isMuted ? "bg-alpha-100" : "bg-alpha-40"
          )}
          icon={isMuted ? MicOff01Icon : Mic01Icon}
        />
      ) : null}
    </div>
  );
}

interface ControlButtonProps {
  label: string;
  onClick: () => void;
  circleClassName: string;
  icon: typeof CallEnd01Icon;
}

function ControlButton({
  label,
  onClick,
  circleClassName,
  icon
}: ControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2"
    >
      <span
        className={cx(
          "grid size-12 place-items-center rounded-full transition",
          circleClassName
        )}
      >
        <HugeiconsIcon icon={icon} size={22} />
      </span>
      <span className="text-xs text-content-400">{label}</span>
    </button>
  );
}
